/**
 * Persistent, user-scoped TanStack Query cache backed by IndexedDB.
 *
 *   - Rehydrates the query cache on mount so first paint renders from disk.
 *   - Refreshes in the background when online; TanStack's normal fetch flow
 *     still runs, so mutations + invalidateQueries behave exactly as before.
 *   - Only "static-until-mutation" queries persist (groups / sets / links /
 *     user-config / contacts list). Everything transient (session, resolver,
 *     notifications, search, impersonation) is opted out so stale data never
 *     corrupts auth or live state.
 *   - The cache key is namespaced by app build + user id so a version bump or
 *     a different user never reads the prior tenant's data.
 */
import { get, set, del, createStore } from 'idb-keyval';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { Query } from '@tanstack/react-query';

const STORE = createStore('slaplink-query-cache', 'pairs');
const APP_VERSION: string = (import.meta as { env?: { VITE_APP_VERSION?: string } }).env?.VITE_APP_VERSION ?? 'dev';

// Top-level query-key names that are safe to persist.
const PERSIST_ALLOWLIST: ReadonlySet<string> = new Set([
  'groups',
  'sets',
  'links',
  'user-config',
  'contacts',
]);

export function shouldDehydrateQuery(q: Query): boolean {
  const root = q.queryKey[0];
  if (typeof root !== 'string') return false;
  if (!PERSIST_ALLOWLIST.has(root)) return false;
  // Skip the ad-hoc "contacts/received", "contacts/sent" - those mutate often.
  if (root === 'contacts') {
    const scope = q.queryKey[1];
    if (scope === 'list' || scope === undefined) return q.state.status === 'success';
    return false;
  }
  return q.state.status === 'success';
}

export function cacheKeyFor(userId: string | null): string {
  return `sl-query-${APP_VERSION}-${userId ?? 'anon'}`;
}

export function makePersister(userId: string | null) {
  const cacheKey = cacheKeyFor(userId);
  return {
    cacheKey,
    persister: createAsyncStoragePersister({
      storage: {
        getItem: (k) => get(k, STORE).then((v) => (v === undefined ? null : (v as string))),
        setItem: (k, v) => set(k, v, STORE),
        removeItem: (k) => del(k, STORE),
      },
      key: cacheKey,
      throttleTime: 1000,
    }),
  };
}

/**
 * Nuke every persisted cache - used on logout or when the signed-in user id
 * changes (e.g. impersonation start/stop) so one tenant never reads another
 * tenant's disk cache.
 */
export async function wipeAllPersistedCaches(): Promise<void> {
  // idb-keyval doesn't expose "clear store", but entries-then-delete is fine.
  const { keys, del: delKey } = await import('idb-keyval');
  const all = await keys(STORE);
  await Promise.all(all.map((k) => delKey(k, STORE)));
}
